import { useContext, useMemo, useRef, useState, useEffect } from "react";
import { v4 } from "uuid";
import SegmentService from "../../../services/SegmentService";
import { TranslationService } from "../../../services/TranslationService";
import CriteriaContext from "../providers/CriteriaContext";
import Criterion from "./Criterion";
import { RequiredManager } from "../../shared/RequieredManager";
import { CriterionModel } from "../providers/CriteriaContext";

type DragData = {
  fromElement: HTMLElement | undefined;
  fromIndex: number;
  toElement: HTMLElement | undefined;
  toIndex: number | undefined;
  originalPosition: ChildNode | null;
};

const Criteria = ({
  requiredManager
}: {
  requiredManager: RequiredManager;
}) => {
  const { translate } = TranslationService;
  const { criteria, addCriterion, reorderCriteria } =
    useContext(CriteriaContext);
  const definitions = useMemo(
    () => SegmentService.getCriteria(translate),
    [translate]
  );
  const [localCriteria, setLocalCriteria] =
    useState<CriterionModel[]>(criteria);
  const dragData = useRef<DragData>({
    fromElement: undefined,
    fromIndex: 0,
    toElement: undefined,
    toIndex: undefined,
    originalPosition: null
  });

  useEffect(() => {
    setLocalCriteria(criteria);
  }, [criteria]);

  const add = () => {
    if (!requiredManager.validate()) {
      return;
    }
    const maxOrder =
      criteria.length > 0
        ? Math.max(...criteria.map((c) => c.Order ?? 0), -1)
        : -1;
    addCriterion({
      key: v4(),
      highlight: false,
      Field: -1,
      Operator: 0,
      Value: "",
      Data: 0,
      ConditionOR: false,
      ActivityTypes: "",
      Order: maxOrder + 1
    });
  };
  if (criteria.length === 0) {
    add();
  }

  const swap = (first: HTMLElement, second: HTMLElement) => {
    if (first === second) {
      return;
    }
    if (first.nextElementSibling === second) {
      first.parentElement?.insertBefore(first, second.nextElementSibling);
    } else {
      first.parentElement?.insertBefore(first, second);
    }
  };

  const preventDefault = (e: {
    preventDefault: () => void;
    stopPropagation: () => void;
  }) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragEndCapture = (e: React.DragEvent<HTMLElement>) => {
    const element = e.currentTarget as HTMLElement;
    element.style.visibility = "visible";
    element.style.opacity = "1";
    if (dragData.current.fromElement === element) {
      element.parentElement?.insertBefore(
        element,
        dragData.current.originalPosition
      );
    }
    dragData.current.fromElement = undefined;
    dragData.current.toElement = undefined;
  };

  const onDragStartCapture = (
    e: React.DragEvent<HTMLElement>,
    index: number
  ) => {
    const element = e.currentTarget as HTMLElement;
    if (element.dataset["noDrag"]) {
      return;
    }
    setTimeout(() => {
      element.style.visibility = "hidden";
      element.style.opacity = "0.5";
    }, 50);
    dragData.current.fromIndex = index;
    dragData.current.fromElement = element;
    dragData.current.toElement = element;
    dragData.current.toIndex = index;
    dragData.current.originalPosition = element.nextSibling;
  };

  const onDragEnterCapture = (
    e: React.DragEvent<HTMLElement>,
    index: number
  ) => {
    const element = e.currentTarget as HTMLElement;
    if (!dragData.current.fromElement || !element.draggable) {
      return;
    }
    if (dragData.current.fromIndex !== index) {
      dragData.current.toIndex = index;
    }
    dragData.current.toElement = element;
    swap(dragData.current.fromElement, element);
  };

  const onDropCapture = (e: React.DragEvent) => {
    if (
      dragData.current.toIndex === undefined ||
      !dragData.current.fromElement ||
      !dragData.current.toElement
    ) {
      return;
    }
    if (dragData.current.fromElement) {
      dragData.current.fromElement.style.visibility = "visible";
      dragData.current.fromElement.style.opacity = "1";
    }
    dragData.current.fromElement = undefined;

    if (!localCriteria || localCriteria.length === 0) {
      return;
    }
    const targetIndex = dragData.current.toIndex;
    const newCriteria = [...localCriteria];
    const item = newCriteria.splice(dragData.current.fromIndex, 1)[0];
    newCriteria.splice(targetIndex, 0, item);
    reorderCriteria(newCriteria);
    setLocalCriteria(newCriteria);
  };

  const getDragEvents = (index: number) => ({
    onDragEnter: (event: React.DragEvent<HTMLElement>) =>
      onDragEnterCapture(event, index),
    onDragStart: (event: React.DragEvent<HTMLElement>) =>
      onDragStartCapture(event, index),
    onDragEnd: onDragEndCapture,
    draggable: true
  });

  return (
    <div onDragOver={preventDefault} onDrop={onDropCapture}>
      {localCriteria.map((item: any, index: number) => (
        <Criterion
          requiredManager={requiredManager}
          key={item.key}
          first={index === 0}
          data={item}
          extraDefinitions={definitions}
          dragEvents={getDragEvents(index)}
          index={index}
        />
      ))}
      <button type="button" className="btn btn-text pt-3" onClick={add}>
        {translate.AddNewCriteria}
      </button>
    </div>
  );
};

export default Criteria;
